import { useDictionary } from "hooks/use-dictionary";
import FiltersAlertBase from "components/filters-alert";
import { PublicEventsFilters } from "types/event";
import dayjs from "dayjs";
import { usePublicOrganizerInstitutionsForEvents } from "hooks/api/use-public-organizer-institutions-for-events";
import { useMemo } from "react";
import { usePublicClassifierValues } from "hooks/api/system-admin/classifiers/use-public-classifier-values";
import { ClassifierType, ClassifierValue } from "types/classifier";

interface FiltersAlertProps {
  filters: PublicEventsFilters;
}

interface InstitutionMap {
  [key: string]: string;
}

const buildClassifiersMap = (classifiers: ClassifierValue[]) => {
  return classifiers.reduce((acc: InstitutionMap, classifier) => {
    acc[classifier.id] = classifier.valueName;
    return acc;
  }, {} as Record<string, string>);
}

const FiltersAlert = ({ filters }: FiltersAlertProps) => {
  const dict = useDictionary();
  const { items: institutions } = usePublicOrganizerInstitutionsForEvents(Boolean(filters.includeExpired));

  const institutionMap = useMemo(() => {
    return institutions.reduce((acc: InstitutionMap, institution) => {
      acc[institution.id] = institution.name;
      return acc;
    }, {} as InstitutionMap);
  }, [institutions]);

  const { items: otherTargetedAudienceClassifiers } = usePublicClassifierValues(ClassifierType.OTHER_TARGETED_AUDIENCE);
  const { items: targetedAudienceClassifiers } = usePublicClassifierValues(ClassifierType.TARGETED_AUDIENCE);
  const { items: developmentCompetencyGroupClassifiers } = usePublicClassifierValues(ClassifierType.DEVELOPMENT_COMPETENCY_GROUP);
  const { items: cities } = usePublicClassifierValues(ClassifierType.CITY);

  const otherTargetedAudienceClassifiersMap = useMemo(() => buildClassifiersMap(otherTargetedAudienceClassifiers), [otherTargetedAudienceClassifiers]);
  const targetedAudienceClassifiersMap = useMemo(() => buildClassifiersMap(targetedAudienceClassifiers), [targetedAudienceClassifiers]);

  const developmentCompetencyGroupClassifiersMap = useMemo(() => buildClassifiersMap(developmentCompetencyGroupClassifiers), [developmentCompetencyGroupClassifiers]);
  const citiesMap = useMemo(() => buildClassifiersMap(cities), [cities]);
  
  return (
    <FiltersAlertBase
      formatters={{
        name: (val) => `${dict.organizerAdmin.events.name} (${val})`,
        startDate: (val) => `${dict.organizerAdmin.events.eventStartDate} (${dayjs(val).format("YYYY-MM-DD")})`,
        endDate: (val) => `${dict.organizerAdmin.events.eventEndDate} (${dayjs(val).format("YYYY-MM-DD")})`,
        organizerInstitution: (val) => {
          if (val !== null) {
            return `${dict.organizerAdmin.events.organizer} (${institutionMap[val] || val})`;
          }
          return "";
        },
        developmentCompetencyGroupClassifier: (val) => filters.developmentCompetencyGroupClassifier ? `${dict.organizerAdmin.events.developmentGroup} (${developmentCompetencyGroupClassifiersMap[filters.developmentCompetencyGroupClassifier]} ${dict.organizerAdmin.events.competencySuffix})` : '',
        programType: (val) => {
          if (val !== null) {
            return `${dict.organizerAdmin.events.type} (${dict.common.programType[val]})`;
          }
          return "";
        },
        eventHours: (val) => `${dict.organizerAdmin.events.duration} (${val})`,
        review: (val) => `${dict.organizerAdmin.events.minimalReview} (${val})`,
        price: (val) => `${dict.organizerAdmin.events.maxPrice} (${val})`,
        targetedAudienceClassifierId: () => filters.targetedAudienceClassifierId ? `${dict.organizerAdmin.events.targetedAudienceClassifier} (${targetedAudienceClassifiersMap[filters.targetedAudienceClassifierId]})` : '',
        otherTargetedAudienceClassifierId: () => filters.otherTargetedAudienceClassifierId ? `${dict.organizerAdmin.events.otherTargetedAudienceClassifier} (${otherTargetedAudienceClassifiersMap[filters.otherTargetedAudienceClassifierId]})` : '',
        city: () => filters.city ? `${dict.organizerAdmin.events.city} (${citiesMap[filters.city]})` : '',
        isRemote: () => dict.organizerAdmin.events.remoteTraining,
        includeExpired: () => dict.common.includeExpiredEvents,
      }}
      filters={filters}
    />
  );
};

export default FiltersAlert;
